// src/lib/auth/logout.js
import supabase from "@/services/supabaseClient";
import { clearLastAuthedPath } from "@/lib/authedPathCache";
import { clearLastRole } from "@/lib/roleCache";
import { writeUserActivityEvent } from "@/services/userTruthTracking.js";

function nukeSupabaseStorage() {
  try {
    const ls = window.localStorage;
    const keys = Object.keys(ls || {});
    for (const k of keys) {
      if (
        (k.startsWith("sb-") && k.endsWith("-auth-token")) ||
        k === "supabase.auth.token" ||
        k.startsWith("supabase.auth.")
      ) {
        ls.removeItem(k);
      }
    }
  } catch {}

  try {
    const ss = window.sessionStorage;
    const keys = Object.keys(ss || {});
    for (const k of keys) ss.removeItem(k);
  } catch {}
}

function withTimeout(promise, ms = 1500) {
  return Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);
}

// One true logout for the whole app.
// - best-effort signOut (never blocks UI)
// - nuke tokens + onboarding crumbs
// - clear role/path hints so stale admin/institution destinations cannot steer next login
// - hard redirect
export async function logout({ redirectTo = "/login?logout=1" } = {}) {
  let currentUser = null;
  try {
    const { data } = await supabase.auth.getUser();
    currentUser = data?.user || null;
  } catch {
    currentUser = null;
  }

  const currentUserId = String(currentUser?.id || "").trim();
  const currentRole = String(
    currentUser?.user_metadata?.primary_role || currentUser?.user_metadata?.role || "",
  ).trim();
  const currentRoute =
    typeof window !== "undefined"
      ? `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`
      : "/logout";

  if (currentUserId) {
    await writeUserActivityEvent({
      userId: currentUserId,
      role: currentRole,
      route: currentRoute,
      eventType: "sign_out_clicked",
      metadata: { redirect_to: redirectTo },
    }).catch(() => {});
  }

  try {
    await withTimeout(supabase.auth.signOut({ scope: "local" }), 1500);
  } catch {
    // ignore — we will hard clear storage + redirect anyway
  }

  if (currentUserId) {
    await writeUserActivityEvent({
      userId: currentUserId,
      role: currentRole,
      route: currentRoute,
      eventType: "sign_out_completed",
      metadata: { redirect_to: redirectTo },
    }).catch(() => {});
  }

  try {
    [
      "onb_employer_v1",
      "onb_moh_v1",
	      "onb_otp_session_v1",
	      "onb_otp_session_v1_patient",
	      "onb_otp_session_v1_doctor",
	      "onb_otp_session_v1_employer",
	      "onb_otp_session_v1_moh",
	      "onb_otp_session_v1_shared",
	      "onb_otp_session_v1_any",
	    ].forEach((k) => localStorage.removeItem(k));
	  } catch {}

  clearLastRole();
  clearLastAuthedPath();

	  nukeSupabaseStorage();

  try {
    window.location.assign(redirectTo);
  } catch {
    window.location.href = redirectTo;
  }
}
